/**
 * Client-side event filtering: default ignore rules plus host-configurable
 * `ignoreErrors` / `denyUrls` / `allowUrls`, matching Sentry's semantics so
 * customers migrating from Sentry can reuse their existing filter config.
 *
 * Portions adapted from sentry-javascript (packages/core/src/integrations/
 * eventFilters.ts and packages/core/src/utils/string.ts), MIT License,
 * Copyright (c) 2019-present Functional Software, Inc. dba Sentry.
 *
 * Pure and dependency-free; covered by __tests__/filters.test.ts.
 */

import { StackFrame } from './stack-parser';

/** A filter pattern: substring match when a string, regex test when a RegExp. */
export type FilterPattern = string | RegExp;

export interface ErrorFilterOptions {
    /**
     * Error messages (or `Type: message` pairs) to drop client-side. Strings
     * match as substrings; RegExps test the full message. Merged with the
     * built-in defaults unless `disableErrorDefaults` is set.
     */
    ignoreErrors?: FilterPattern[];
    /** Drop errors whose originating script URL matches any pattern. */
    denyUrls?: FilterPattern[];
    /** When set, only keep errors whose originating script URL matches. */
    allowUrls?: FilterPattern[];
    /** Opt out of the built-in default ignore list. */
    disableErrorDefaults?: boolean;
}

/**
 * Sentry's curated default ignore list (browser noise nobody can act on),
 * plus HumanBehavior's own transport/self noise.
 */
export const DEFAULT_IGNORE_ERRORS: FilterPattern[] = [
    // From sentry-javascript DEFAULT_IGNORE_ERRORS (MIT):
    /^Script error\.?$/,
    /^Javascript error: Script error\.? on line 0$/,
    /^ResizeObserver loop completed with undelivered notifications.$/,
    /^Cannot redefine property: googletag$/,
    /^Can't find variable: gmo$/,
    /^undefined is not an object \(evaluating 'a\.[A-Z]'\)$/,
    /can't redefine non-configurable property "solana"/,
    /vv\(\)\.getRestrictions is not a function/,
    /Can't find variable: _AutofillCallbackHandler/,
    /Object Not Found Matching Id:\d+, MethodName:simulateEvent/,
    /^Java exception was raised during method invocation$/,
    // HumanBehavior SDK's own surfaced noise (kept in sync with the console
    // suppression list in tracker.ts) so we never report ourselves.
    /HumanBehavior error/i,
    /Failed to send events/i,
    // Ingest transport blips: flush persists + retries; surfacing these as
    // Runtime Issues just mirrors our own outbound failures.
    /request timeout/i,
    /^TimeoutError\b/,
];

/** Substring match for strings, `.test()` for RegExps (Sentry semantics). */
export function isMatchingPattern(value: string, pattern: FilterPattern): boolean {
    if (typeof value !== 'string') {
        return false;
    }
    if (pattern instanceof RegExp) {
        return pattern.test(value);
    }
    return value.includes(pattern);
}

export function stringMatchesSomePattern(value: string, patterns: FilterPattern[] = []): boolean {
    return patterns.some((pattern) => isMatchingPattern(value, pattern));
}

/** The message forms an ignore rule can match against (Sentry checks both). */
function possibleMessages(exceptionType: string, value: string): string[] {
    const messages: string[] = [];
    if (value) {
        messages.push(value);
    }
    if (exceptionType && value) {
        messages.push(`${exceptionType}: ${value}`);
    }
    return messages;
}

/**
 * The URL to filter on: the last stack frame with a real (non-anonymous)
 * filename — the script the error actually originated from (Sentry's rule).
 */
export function reportUrlForFiltering(frames: StackFrame[]): string | null {
    for (let i = frames.length - 1; i >= 0; i -= 1) {
        const file = frames[i]?.file;
        if (file && file !== '<anonymous>' && file !== '[native code]') {
            return file;
        }
    }
    return null;
}

export interface FilterableReport {
    exceptionType: string;
    value: string;
    stackFrames: StackFrame[];
}

export interface DropReason {
    reason: 'ignoreErrors' | 'denyUrls' | 'allowUrls';
    matched: string;
}

/**
 * Returns why a report should be dropped, or null to keep it. Applies, in
 * Sentry's order: ignore-message patterns, denied URLs, then the allow-list.
 */
export function getDropReason(report: FilterableReport, options: ErrorFilterOptions = {}): DropReason | null {
    const ignore = [
        ...(options.ignoreErrors || []),
        ...(options.disableErrorDefaults ? [] : DEFAULT_IGNORE_ERRORS),
    ];
    for (const message of possibleMessages(report.exceptionType, report.value)) {
        if (stringMatchesSomePattern(message, ignore)) {
            return { reason: 'ignoreErrors', matched: message };
        }
    }

    const url = reportUrlForFiltering(report.stackFrames);
    if (options.denyUrls?.length && url && stringMatchesSomePattern(url, options.denyUrls)) {
        return { reason: 'denyUrls', matched: url };
    }
    if (options.allowUrls?.length && url && !stringMatchesSomePattern(url, options.allowUrls)) {
        return { reason: 'allowUrls', matched: url };
    }
    return null;
}
